/* eslint-disable @angular-eslint/no-host-metadata-property */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CONFIG } from '@domgen/dgx-fe-config';
import { isSameDay } from 'date-fns';
import { MMRClaimEvent } from '../../../containers/manage-my-repair/manage-my-repair.container';
import { IrelandBasedCompanies } from '../non-active-repair-state/irish-companies';
import { isBaxiManufacturer } from '../../data/baxi-manufacturer-names';
import {
  MMRClaimDetail,
  ManageMyRepairResponse,
  CallCostType,
  ServiceOptionTypeEnum,
  MMRClaimDetailRepairer,
  AvailableClaimType,
} from '../../../types/manage-my-repair-types';
import {
  ClaimType,
  RepairAction,
  RepairActionEnum,
  ServiceAction,
  ServiceActionEnum,
} from '../../../types/plan-detail.types';
import { ClaimStatusTypeEnum } from '../../../types/applianceTypeEnum';
import { AppConfig } from '../../../types/app-config.model';
import { PcaService, FeatureFlagService } from '@domgen/dgx-components';
import { ManageMyRepairService } from '../../../services/manage-my-repair.service';
import { CorrespondenceAddress } from '@domgen/common-person-types';

@Component({
  selector: 'claims-athome-myaccount-manage-my-repair-claim-detail-container',
  templateUrl: './claim-detail-container.component.html',
  styleUrls: ['./claim-detail-container.component.scss'],
  host: {
    '[class]': 'getClasses()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageMyRepairClaimDetailContainerComponent implements OnChanges {
  @Input() claimDetails: MMRClaimDetail;

  @Input() claimType: ClaimType;

  @Input() fullClaimsDetails: ManageMyRepairResponse;

  @Input() lastUpdated: Date | undefined;

  @Input() isRPC: boolean;

  @Input() address: CorrespondenceAddress | undefined;

  @Input() email?: string;

  @Input() isNewOrbitFlow: boolean;

  @Output() cancelClaim: EventEmitter<MMRClaimEvent> =
    new EventEmitter<MMRClaimEvent>();

  @Output() createClaim: EventEmitter<MMRClaimEvent> =
    new EventEmitter<MMRClaimEvent>();

  @Output() rebookClaim: EventEmitter<MMRClaimEvent> =
    new EventEmitter<MMRClaimEvent>();

  @Output() trackClaim: EventEmitter<string> = new EventEmitter<string>();

  claimDisplayDetails: DisplayDetails | undefined;

  displayNonCancelRebookBlock = false;

  repairActionEnum = RepairActionEnum;

  callCostType: CallCostType | undefined;

  titleKey: string;

  groupTitle: string;

  displayClaimStatus: WorkDisplayStatus;

  isNonBookableProductType = false;

  isSPToContactCustomerFlow = false;

  isLoading = false;

  claimTypes = [];

  selectedManufacturer: string | undefined;

  private isAsvEligibleByManufacturer(): boolean {
    const isBaxi = isBaxiManufacturer(
      this.fullClaimsDetails?.item?.manufacturer
    );
    const asvConfig = this.featureFlagService.getFeatureFlagPayload(
      'claims-2-0-asv-enabled' as any
    ) as {
      baxiEnabled?: boolean;
      nonBaxiEnabled?: boolean;
    } | null;

    const baxiAsvEnabled = asvConfig?.baxiEnabled ?? false;
    const nonBaxiAsvEnabled = asvConfig?.nonBaxiEnabled ?? false;

    return isBaxi ? baxiAsvEnabled : nonBaxiAsvEnabled;
  }

  get getCanShowNeedRepair(): boolean {
    if (!this.isNewOrbitFlow) {
      return false;
    }

    if (this.claimType === 'SERVICE') {
      const serviceAction = this.fullClaimsDetails?.serviceAction;
      const isServiceEligible =
        serviceAction === ServiceActionEnum.REQUEST_SERVICE;
      return isServiceEligible && this.isAsvEligibleByManufacturer();
    }

    const repairAction = this.fullClaimsDetails?.repairAction;

    return (
      repairAction === RepairActionEnum.REQUEST_REPAIR ||
      repairAction === RepairActionEnum.IN_WARRANTY
    );
  }

  get canUseOrbitFlow(): boolean {
    if (!this.isNewOrbitFlow) {
      return false;
    }

    // For SERVICE, only allow annual service states based on ASV feature flags
    if (this.claimType === 'SERVICE') {
      const serviceAction = this.fullClaimsDetails?.serviceAction;
      const isServiceEligible =
        serviceAction === ServiceActionEnum.REQUEST_SERVICE ||
        serviceAction === ServiceActionEnum.INCOMPLETE_FORM ||
        serviceAction === ServiceActionEnum.BOOK_ENGINEER;
      return isServiceEligible && this.isAsvEligibleByManufacturer();
    }

    // For REPAIR, allow if new orbit flow is enabled
    return true;
  }

  getClasses(): string {
    return this.claimDisplayDetails?.showDetailsBreakdown ? 'full-width' : '';
  }

  get action(): RepairAction | ServiceAction {
    if (this.claimType === 'REPAIR') return this.fullClaimsDetails.repairAction;
    return this.fullClaimsDetails.serviceAction;
  }

  get isClaimBeforeBookingStage(): boolean {
    if (this.claimType === 'REPAIR') {
      return (
        this.action === RepairActionEnum.BOOK_ENGINEER ||
        this.action === RepairActionEnum.INCOMPLETE_FORM
      );
    }

    return (
      this.action === ServiceActionEnum.BOOK_ENGINEER ||
      this.action === ServiceActionEnum.INCOMPLETE_FORM
    );
  }

  get incompleteBooking(): boolean {
    if (this.claimType === 'REPAIR') {
      return (
        this.fullClaimsDetails?.repairAction ===
          this.repairActionEnum.INCOMPLETE_FORM ||
        this.fullClaimsDetails?.repairAction ===
          this.repairActionEnum.BOOK_ENGINEER
      );
    }

    return (
      this.fullClaimsDetails.serviceAction ===
        ServiceActionEnum.INCOMPLETE_FORM ||
      this.fullClaimsDetails.serviceAction == ServiceActionEnum.BOOK_ENGINEER
    );
  }

  get overviewText(): string {
    if (this.claimType !== 'SERVICE') return '';

    const { repairPaymentType, planInWaitPeriod } = this.fullClaimsDetails;

    if (
      this.isCalloutOrExcessClaim() &&
      !planInWaitPeriod &&
      repairPaymentType === 'CALLOUT'
    ) {
      return '';
    }

    return `MMR.SECTION.${this.claimType}.OVERVIEW`;
  }

  private isCalloutOrExcessClaim(): boolean {
    const { repairAction, serviceAction, repairPayment, repairPaymentType } =
      this.fullClaimsDetails;

    if (
      repairPayment === 'NO' ||
      !repairPaymentType ||
      !['EXCESS', 'CALLOUT'].includes(repairPaymentType)
    ) {
      return false;
    }

    const repairStatuses: RepairAction[] = [
      RepairActionEnum.BOOK_ENGINEER,
      RepairActionEnum.INCOMPLETE_FORM,
      RepairActionEnum.REQUEST_REPAIR,
    ];

    const serviceStatuses: ServiceAction[] = [
      ServiceActionEnum.BOOK_ENGINEER,
      ServiceActionEnum.INCOMPLETE_FORM,
      ServiceActionEnum.REQUEST_SERVICE,
    ];

    const statusMatch: Partial<Record<ClaimType, boolean>> = {
      REPAIR: repairStatuses.includes(repairAction),
      SERVICE: serviceStatuses.includes(serviceAction),
    };

    return statusMatch[this.claimType] ?? false;
  }

  private checkClaimConditions(): boolean {
    const isRepairBookingNeeded =
      this.fullClaimsDetails?.openRepair?.claimStatus ===
        ClaimStatusTypeEnum.RepairBookingNeeded && this.claimType === 'REPAIR';

    const isServiceOrRepairInWait =
      this.fullClaimsDetails?.serviceAction === RepairActionEnum.IN_WAIT ||
      this.fullClaimsDetails?.repairAction === RepairActionEnum.IN_WAIT;

    const isServiceOrRepairInWarranty =
      this.fullClaimsDetails?.serviceAction === RepairActionEnum.IN_WARRANTY ||
      this.fullClaimsDetails?.repairAction === RepairActionEnum.IN_WARRANTY;

    const isSpecialItemCode = this.fullClaimsDetails?.item?.code === 'X7';

    return (
      isRepairBookingNeeded ||
      isServiceOrRepairInWait ||
      isServiceOrRepairInWarranty ||
      isSpecialItemCode
    );
  }

  private getStatusText(): string {
    const isNonActiveState = this.isNonActiveState;

    if (
      this.isDirectReferralState() ||
      this.checkClaimConditions() ||
      this.isNonBookableProductType ||
      this.isSPToContactCustomer(this.action)
    ) {
      return '';
    }

    if (isNonActiveState) {
      return `MMR.SECTION.${this.claimType}.${this.action}`;
    }

    if (
      isSameDay(
        new Date(),
        new Date(this.claimDetails?.appointmentDateSelected)
      )
    ) {
      return `MMR.SECTION.${this.claimType}.TODAY_OVERVIEW`;
    }

    if (
      this.claimDetails &&
      Object.values(ServiceOptionTypeEnum).includes(
        this.claimDetails.serviceOptionType
      )
    ) {
      return 'MMR.SECTION.REPAIR.NON_ENGINEER';
    }

    return `MMR.SECTION.${this.claimType}.OVERVIEW`;
  }

  private isDirectReferralState(): boolean {
    const { repairAction } = this.fullClaimsDetails;
    const referralStatuses: string[] = [
      RepairActionEnum.DIRECT_CALL_CENTRE,
      RepairActionEnum.DIRECT_REFERRAL_ONLY,
    ];

    if (referralStatuses.includes(repairAction)) {
      return true;
    }

    return false;
  }

  get isNonActiveState(): boolean {
    const openClaim =
      this.claimType === 'REPAIR'
        ? this.fullClaimsDetails?.openRepair
        : this.fullClaimsDetails?.openService;

    const claimStatus = openClaim?.claimStatus || '';
    const isWhp = openClaim?.bookingOEM === 'whirlpool';
    const nonBookingProductTypesList = ['HE', 'PD', 'FC'];
    this.isNonBookableProductType = nonBookingProductTypesList.includes(
      this.fullClaimsDetails?.productType
    );
    this.isSPToContactCustomerFlow = this.isSPToContactCustomer(this.action);

    const statusToShowNonActiveClaim: string[] = [
      RepairActionEnum.BOOK_ENGINEER,
      RepairActionEnum.INCOMPLETE_FORM,
      RepairActionEnum.REQUEST_REPAIR,
      RepairActionEnum.UNKNOWN,
      RepairActionEnum.IN_WAIT,
      RepairActionEnum.IN_WARRANTY,
      RepairActionEnum.DIRECT_CALL_CENTRE,
      RepairActionEnum.DIRECT_REFERRAL_ONLY,
      ServiceActionEnum.REQUEST_SERVICE,
    ];

    if (
      this.claimType === 'SERVICE' &&
      this.fullClaimsDetails.openService &&
      !this.isSPToContactCustomerFlow &&
      !this.incompleteBooking
    ) {
      return false;
    }

    return (
      this.fullClaimsDetails.planInWaitPeriod ||
      statusToShowNonActiveClaim.includes(this.action) ||
      this.isCalloutOrExcessClaim() ||
      this.fullClaimsDetails?.item?.code === 'X7' ||
      (isWhp && claimStatus === ClaimStatusTypeEnum.RepairBookingNeeded) ||
      this.isNonBookableProductType ||
      this.isSPToContactCustomerFlow
    );
  }

  constructor(
    @Inject(CONFIG) private readonly config: AppConfig,
    private readonly pcaService: PcaService,
    private readonly cdr: ChangeDetectorRef,
    private readonly mmrService: ManageMyRepairService,
    private readonly featureFlagService: FeatureFlagService
  ) {
    this.isLoading = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.claimDetails) {
      this.claimTypes = this.getPlanClaims();

      if (this.claimDetails?.trackable) {
        this.setWorkStatuses();
      }
      this.setTitleAndGroupTitle();

      if (this.claimType === 'REPAIR') {
        this.getRepairDisplayData();
      } else {
        this.getServiceDisplayData();
      }
    }

    if (changes.fullClaimsDetails) {
      const manufacturer =
        this.fullClaimsDetails?.item?.manufacturer?.trim() ?? '';
      this.selectedManufacturer =
        manufacturer.length > 0 && manufacturer.toLowerCase() !== 'unknown'
          ? manufacturer
          : undefined;
    }
  }

  hasServiceDetails(): boolean {
    if (!this.fullClaimsDetails) return false;

    const { serviceAction, openService } = this.fullClaimsDetails;

    const showServiceOptionStatus: string[] = [
      ServiceActionEnum.REQUEST_SERVICE,
      ServiceActionEnum.AMEND_SERVICE,
      ServiceActionEnum.INCOMPLETE_FORM,
      ServiceActionEnum.BOOK_ENGINEER,
      ServiceActionEnum.IN_WAIT,
      ServiceActionEnum.IN_WARRANTY,
      ...(openService ? [ServiceActionEnum.NO_ACTION] : []),
    ];

    return showServiceOptionStatus.includes(serviceAction);
  }

  getPlanClaims(): AvailableClaimType[] {
    const action =
      this.claimType === 'REPAIR'
        ? this.fullClaimsDetails.repairAction
        : this.fullClaimsDetails.serviceAction;

    // if in warranty
    if (
      this.fullClaimsDetails.planInWarrantyPeriod ||
      action === 'IN_WARRANTY'
    ) {
      // if theres no available claim types then default to BD
      return !this.fullClaimsDetails.availableClaimTypes?.length
        ? this.mmrService.getClaimTypes([
            {
              claimTypeID: '3',
              claimTypeCode: 'BD',
              claimTypeName: 'Breakdown',
              claimTypeLabel: 'Breakdown',
            },
          ])
        : this.mmrService.getClaimTypes(
            this.fullClaimsDetails.availableClaimTypes ?? []
          );
    } else {
      const filteredClaimTypes =
        this.claimType === 'SERVICE'
          ? (this.fullClaimsDetails.availableClaimTypes ?? []).filter(
              (ct) => ct.claimTypeCode === 'ASV'
            )
          : (this.fullClaimsDetails.availableClaimTypes ?? []).filter(
              (ct) => ct.claimTypeCode !== 'ASV'
            );
      return this.mmrService.getClaimTypes(filteredClaimTypes ?? []);
    }
  }

  private setWorkStatuses(): void {
    if (this.claimDetails?.trackable) {
      this.displayClaimStatus = {
        key: 'TRACK',
        icon: 'truck-fast',
        class: 'success-track',
      };
    }
  }

  private setTitleAndGroupTitle(): void {
    this.setTitles('.NON_CANCEL_REBOOK.TITLE', 'CHANGE_BOOKING');
  }

  private setTitles(titleSuffix: string, groupTitleKey: string): void {
    this.titleKey = 'MMR.SECTION.' + this.claimDetails?.claimType + titleSuffix;
    this.groupTitle = groupTitleKey;
  }

  getRepairDisplayData(): void {
    this.getClaimPhoneChargeText(this.fullClaimsDetails.openRepair?.repairer);
    if (
      this.fullClaimsDetails.item.code === 'X7' ||
      this.fullClaimsDetails.planInWaitPeriod
    ) {
      this.claimDisplayDetails = {
        showDetailsBreakdown: false,
        actionText: '',
        config: {},
      };
      return;
    }

    const { item, inWarrantyContactName, inWarrantyContactTel } =
      this.fullClaimsDetails;
    const customRepairActionMappers = {
      TRACK_REPAIR: () => {
        if (
          this.config.vokeraBrands.some(
            (i) => item.manufacturer.toLowerCase().indexOf(i) > -1
          )
        ) {
          return 'VOKERA_BRANDS';
        }

        return 'TRACK_REPAIR';
      },
    };

    const customTextFormatters = {
      IN_WARRANTY: () => {
        if (inWarrantyContactName) {
          if (inWarrantyContactTel) {
            return 'IN_WARRANTY';
          }
          return 'IN_WARRANTY_WITHOUT_TEL';
        }
        return 'IN_WARRANTY_WITHOUT_TEL_AND_CONTACT';
      },
      TRACK_REPAIR: () => 'REBOOK_VISIT',
    };

    let repairAction = this.fullClaimsDetails.repairAction;

    let showDetailsBreakdown = false;

    if (
      repairAction === RepairActionEnum.TRACK_REPAIR ||
      (repairAction === RepairActionEnum.NO_ACTION &&
        this.fullClaimsDetails.openRepair)
    ) {
      showDetailsBreakdown = true;
    }

    if (customRepairActionMappers[repairAction]) {
      repairAction = customRepairActionMappers[repairAction]();
    }

    let actionText = `Plan_Details_Page.3.${repairAction}`;

    if (customTextFormatters[repairAction]) {
      actionText = `Plan_Details_Page.3.${customTextFormatters[
        repairAction
      ]()}`;
    }

    const statusText = this.getStatusText();

    this.claimDisplayDetails = {
      statusText,
      showDetailsBreakdown,
      actionText,
      config: {
        inWarrantyContactName: inWarrantyContactName,
        inWarrantyContactTel: inWarrantyContactTel,
        manufacturer: item.manufacturer,
      },
    };
  }

  getServiceDisplayData(): void {
    this.getClaimPhoneChargeText(this.fullClaimsDetails.openService?.repairer);
    const { serviceAction } = this.fullClaimsDetails;

    let showDetailsBreakdown = false;
    const statusText = this.getStatusText();

    const actionMap = {
      REQUEST_SERVICE: 'BOOK_ASV',
      AMEND_SERVICE: 'REBOOK_VISIT',
      INCOMPLETE_FORM: 'COMPLETE_ASV',
      BOOK_ENGINEER: 'COMPLETE_ASV',
      IN_WAIT: 'WAITING_WARRANTY_ASV',
      IN_WARRANTY: 'WAITING_WARRANTY_ASV',
      NO_ACTION: 'NO_ACTION',
    };

    if (
      serviceAction === ServiceActionEnum.AMEND_SERVICE ||
      (serviceAction === ServiceActionEnum.NO_ACTION &&
        this.fullClaimsDetails.openService)
    ) {
      showDetailsBreakdown = true;
    }

    if (actionMap[serviceAction]) {
      const baseClaimDetails = {
        showDetailsBreakdown,
        actionText: `Plan_Details_Page.asv.${actionMap[serviceAction]}`,
        config: {},
      };
      this.claimDisplayDetails =
        serviceAction !== ServiceActionEnum.NO_ACTION
          ? { ...baseClaimDetails, statusText }
          : baseClaimDetails;
    }
  }

  getClaimPhoneChargeText(repairerDetails: MMRClaimDetailRepairer): void {
    if (!repairerDetails) return;

    const { phone_number: telephone, postCode } = repairerDetails;
    if (telephone === '' && postCode === '') return;
    if ((telephone || '').startsWith('0333')) {
      this.callCostType = 'STANDARD_MESSAGE';
      return;
    }

    if (postCode === '') {
      return;
    }

    this.pcaService.pcaFind(postCode, '', ['GB', 'IRL']).subscribe({
      next: (val) => {
        if (val.Items.length === 0 || val.Items[0]?.Id?.startsWith('GB')) {
          this.callCostType = 'UK';
          this.cdr.detectChanges();
          return;
        }

        if (val.Items[0].Id.startsWith('IE')) {
          this.callCostType = 'IRELAND';
        }
        this.cdr.detectChanges();
      },
      error: () => {
        if (repairerDetails.name) {
          const irishBusiness = IrelandBasedCompanies.find(
            (i) => i.toLowerCase() === repairerDetails.name.toLowerCase()
          );

          if (irishBusiness) {
            this.callCostType = 'IRELAND';
            return;
          }
        }

        this.callCostType = 'UK';
      },
    });
  }

  isSPToContactCustomer(action: string): boolean {
    const statusName = this.claimDetails?.claimStatusName;

    const statusesForSPFlow: string[] = [
      'REPAIR - JOB VIEWED BY SERVICE PROVIDER',
      'REPAIR - JOB COMMUNICATED TO SERVICE PROVIDER',
      'REPAIR - CUSTOMER NOT AVAIL (MESSAGE LEFT)',
      'REPAIR - INFORMATION RECEIVED',
      '1ST FIELD CALL REQUIRED', // for when claim is part of SP flow but SP hasn't confirmed receipt
    ];

    return (
      (action === RepairActionEnum.TRACK_REPAIR ||
        action === ServiceActionEnum.NO_ACTION) &&
      statusesForSPFlow.includes(statusName) &&
      this.claimDetails?.appointmentDateSelected === null &&
      !this.isNonEngineerOption &&
      this.claimDetails?.serviceOptionType !== 'Self Fit'
    );
  }

  get isNonEngineerOption(): boolean {
    const nonEngineerOptions: string[] = [
      ServiceOptionTypeEnum.Courier,
      ServiceOptionTypeEnum['Self Send'],
      ServiceOptionTypeEnum['Drop off'],
    ];

    return nonEngineerOptions.includes(this.claimDetails.serviceOptionType);
  }
}

export type DisplayDetails = {
  showDetailsBreakdown: boolean;
  actionText: string;
  config: Record<string, unknown>;
  statusText?: string;
};

export interface WorkDisplayStatus {
  key: string;
  icon: string;
  class: string;
}
